import { motion } from "framer-motion";
import { studio } from "@/data/projects";
import { getLenis } from "@/hooks/useSmoothScroll";

const EASE = [0.25, 0.1, 0.25, 1];

// Masked line-by-line reveal
function Reveal({ children, delay = 0, className = "" }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className={`block ${className}`}
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.95, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

const draw = (delay) => ({
  initial: { pathLength: 0, opacity: 0 },
  animate: { pathLength: 1, opacity: 1 },
  transition: { pathLength: { duration: 2.2, ease: EASE, delay }, opacity: { duration: 0.4, delay } },
});

const nodePop = (delay) => ({
  initial: { scale: 0, opacity: 0 },
  animate: { scale: 1, opacity: 1 },
  transition: { duration: 0.6, ease: EASE, delay },
});

export default function Hero() {
  const scrollDown = () => {
    const el = document.getElementById("projects");
    const lenis = getLenis();
    if (el && lenis) lenis.scrollTo(el, { offset: -20, duration: 1.4 });
  };

  return (
    <section
      className="relative flex min-h-[100svh] w-full items-center justify-center overflow-hidden"
      data-testid="hero"
    >
      {/* Construction-line animation layer */}
      <svg
        className="pointer-events-none absolute inset-0 h-full w-full"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden
      >
        {/* long horizontals */}
        <motion.line x1="0" y1="220" x2="1440" y2="220" stroke="#1B3818" strokeWidth="1" {...draw(0.3)} />
        <motion.line x1="0" y1="680" x2="1440" y2="680" stroke="#000" strokeWidth="1" opacity="0.35" {...draw(0.6)} />
        {/* verticals */}
        <motion.line x1="360" y1="0" x2="360" y2="900" stroke="#000" strokeWidth="1" opacity="0.12" {...draw(0.9)} />
        <motion.line x1="1080" y1="0" x2="1080" y2="900" stroke="#000" strokeWidth="1" opacity="0.12" {...draw(1.05)} />
        {/* diagonal construction guide */}
        <motion.line x1="360" y1="680" x2="1080" y2="220" stroke="#1B3818" strokeWidth="1" opacity="0.5" {...draw(1.4)} />
        {/* circle guide */}
        <motion.circle
          cx="720"
          cy="450"
          r="230"
          fill="none"
          stroke="#000"
          strokeWidth="1"
          opacity="0.1"
          {...draw(1.1)}
        />

        {/* intersection nodes */}
        {[
          [360, 220],
          [1080, 220],
          [360, 680],
          [1080, 680],
          [720, 450],
        ].map(([cx, cy], i) => (
          <motion.circle
            key={i}
            cx={cx}
            cy={cy}
            r="3.5"
            fill="#1B3818"
            {...nodePop(1.6 + i * 0.12)}
          />
        ))}

        {/* traveling node along top horizontal */}
        <motion.circle
          cy="220"
          r="4"
          fill="#1B3818"
          initial={{ cx: 0, opacity: 0 }}
          animate={{ cx: 1440, opacity: [0, 1, 1, 0] }}
          transition={{ duration: 7, ease: "linear", delay: 2.4, repeat: Infinity }}
        />
        <motion.circle
          cy="680"
          r="3"
          fill="#000"
          initial={{ cx: 1440, opacity: 0 }}
          animate={{ cx: 0, opacity: [0, 0.5, 0.5, 0] }}
          transition={{ duration: 9, ease: "linear", delay: 3, repeat: Infinity }}
        />
      </svg>

      {/* Content */}
      <div className="relative z-10 w-full px-6 text-center md:px-12">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mx-auto mb-10 flex items-center justify-center gap-4"
        >
          <span className="h-px w-10 bg-[#1B3818]" />
          <span className="ama-label text-black/50">{studio.location} — Est. MMXXII</span>
          <span className="h-px w-10 bg-[#1B3818]" />
        </motion.div>

        <h1
          className="font-light leading-[0.92] tracking-tight"
          data-testid="hero-title"
        >
          <span className="block text-[12vw] sm:text-[10vw] md:text-[7rem] lg:text-[8rem]">
            <Reveal delay={0.35}>Ante Meridian</Reveal>
          </span>
          <span className="block text-[12vw] sm:text-[10vw] md:text-[7rem] lg:text-[8rem]">
            <Reveal delay={0.5}>Architects</Reveal>
          </span>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 1.2 }}
          className="mx-auto mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 ama-label text-black/70"
        >
          {studio.disciplines.map((d, i) => (
            <span key={d} className="flex items-center gap-6">
              {d}
              {i < studio.disciplines.length - 1 && (
                <span className="h-1 w-1 rounded-full bg-[#1B3818]" />
              )}
            </span>
          ))}
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="mx-auto mt-8 max-w-md text-sm font-light leading-relaxed text-black/50"
        >
          A Dubai practice designing architecture that balances clarity, context and experience.
        </motion.p>
      </div>

      {/* scroll cue */}
      <motion.button
        data-testid="hero-scroll"
        data-cursor="link"
        onClick={scrollDown}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.8 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
      >
        <span className="ama-label text-black/40">Scroll</span>
        <motion.div
          className="mx-auto mt-3 h-8 w-px bg-black/30"
          animate={{ scaleY: [0.3, 1, 0.3], originY: 0 }}
          transition={{ duration: 2.4, ease: EASE, repeat: Infinity }}
        />
      </motion.button>
    </section>
  );
}
